import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  User, Users, Timer, BookOpen, IndianRupee, CalendarDays,
  BadgeCheck, Inbox, BarChart3, Link2, Video, Award, ImageIcon,
  RefreshCw, Star, FileText, UserPlus, FileCheck,
} from 'lucide-react';
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import {
  ServiceHero, JumpNav, SplitSection, StepsSection, CardsSection,
  FormatsSection, PanelSection, CompareSection, CtaBlock, FaqSection, RelatedPages,
  type FaqItem, type RelatedPage,
} from "@/components/service-page/ServicePageSections";

const KEYWORDS = [
  "become an online tutor", "online teaching platform for tutors",
  "register as an online tutor", "online tutoring jobs in India",
  "teach online and earn money", "how to become an online tutor in India",
];

const BECOME_TUTOR = { label: "Become a Tutor", to: "/how-it-works/apply" };

const JUMP_ITEMS = [
  { id: "what",    label: "What it means" },
  { id: "why",     label: "Why join" },
  { id: "formats", label: "Teaching formats" },
  { id: "steps",   label: "How to start" },
  { id: "tools",   label: "Tools" },
  { id: "profile", label: "Your profile" },
  { id: "faq",     label: "FAQs" },
];

const RELATED: RelatedPage[] = [
  {
    label: "1-on-1 Tutoring",
    color: "#2F6BD8",
    icon: User,
    title: "Find 1-on-1 Private Tutors for Personalised Learning",
    excerpt: "See how students find and book private sessions — understand what they look for.",
    to: "/private-tutoring-sessions",
  },
  {
    label: "Group Classes",
    color: "#0E8A78",
    icon: Users,
    title: "Join Group Classes for School, Exams & Skills",
    excerpt: "See how students browse group batches — useful context for planning your own.",
    to: "/online-group-classes",
  },
  {
    label: "Crash Courses",
    color: "#D9472B",
    icon: Timer,
    title: "Exam Crash Courses — Fixed Sessions, Clear Timetable",
    excerpt: "See how students compare crash courses — helps you write a clear listing.",
    to: "/exam-crash-courses",
  },
];

const faqs: FaqItem[] = [
  {
    question: "How can I become an online tutor in India?",
    answer: "Create a teacher account, complete your profile and submit it for review. Once approved, publish an offering with your subject, format, fee and schedule.",
  },
  {
    question: "How do I register as a tutor on BookMyCoaching?",
    answer: "Create a teacher account and complete the requested registration and verification steps. Add accurate qualifications and experience, then submit your profile for review.",
  },
  {
    question: "What qualifications do I need to become an online tutor?",
    answer: "Requirements vary by subject and learner level. Add relevant education, certificates, skills and teaching experience so your background is clear.",
  },
  {
    question: "Can I set my own fees and teaching schedule?",
    answer: "Yes. Tutors choose their prices and availability for each offering. You can publish session rates, group fees or a crash course package.",
  },
  {
    question: "Can I teach both individual students and group classes?",
    answer: "Yes. You can offer 1-on-1 sessions, group classes and crash courses. Describe each format clearly so students know what they are enquiring about.",
  },
  {
    question: "Is creating a tutor profile on BookMyCoaching free?",
    answer: "Check the current registration and subscription details on BookMyCoaching before signing up. Review the terms to see what is included and whether any charges apply.",
  },
  {
    question: "How do tutors receive student enquiries?",
    answer: "Students can find published offerings and send questions through the relevant listing. You can read and reply through the platform inbox.",
  },
  {
    question: "What features are available to tutors on BookMyCoaching?",
    answer: "Tools include a reviewed profile, introductory video, offering builder, inbox, analytics and a custom profile link. Gold and Platinum subscriptions may include additional class management and communication tools.",
  },
];

export default function BecomeOnlineTutorPage() {
  const { pathname } = useLocation();
  const title = 'Become an Online Tutor in India | BookMyCoaching';
  const description = "Register as an online tutor on BookMyCoaching. Set your fees, choose your schedule, publish classes and reply to student enquiries from one dashboard.";

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={KEYWORDS.join(", ")} />
        <link rel="canonical" href={getCanonicalUrl(pathname)} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={getCanonicalUrl(pathname)} />
        <meta property="og:image" content={getDefaultOgImage()} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <Header />

      <main>
        <ServiceHero
          badge="Teach on BookMyCoaching"
          titleLead="Become an Online Tutor in India"
          titleHighlight="with BookMyCoaching"
          intro="Set your fees. Choose your schedule. Connect with students. Teach subjects you know, create classes that suit your style, and share your expertise with learners looking for guidance."
          primary={BECOME_TUTOR}
          secondary={{ label: "How it works", to: "#steps" }}
          facts={[
            { icon: IndianRupee, label: "You set your fees" },
            { icon: CalendarDays, label: "You choose your schedule" },
            { icon: BadgeCheck, label: "Reviewed profile" },
          ]}
          image="/bookmycoaching-become-a-tutor.gif"
        />

        <JumpNav items={JUMP_ITEMS} />

        {/* What */}
        <SplitSection
          id="what"
          title="What Does It Mean to Teach on BookMyCoaching?"
          imagePlaceholder="Indian teacher at a tidy home desk with laptop and headset, mid-explanation, bookshelf behind"
          paras={[
            "BookMyCoaching is a tutoring marketplace where teachers create reviewed profiles, publish classes and respond to student enquiries. You choose your subjects, teaching format, availability and fees.",
            "Your profile helps learners assess your experience and classes. BookMyCoaching works as an online teaching platform for tutors who want to showcase their classes and connect with interested learners.",
          ]}
          bullets={[
            "Create a reviewed teacher profile with your qualifications",
            "Publish 1-on-1 sessions, group classes or crash courses",
            "Set your own fees and availability",
            "Respond to student enquiries through the platform inbox",
          ]}
          paras2={[
            "To become an online tutor, start with the subjects you can teach confidently. Keep your profile clear and honest so students know what to expect.",
          ]}
        />

        {/* Why */}
        <CardsSection
          id="why"
          title="Why Become an Online Tutor with BookMyCoaching?"
          intro="Build your tutoring presence around your skills and preferred working style. You can choose the format and schedule that fit your availability."
          shaded
          cols={3}
          cards={[
            { icon: IndianRupee,  title: "Set your fees",        description: "Price individual sessions, group classes or course packages. You control your rates." },
            { icon: CalendarDays, title: "Choose your schedule", description: "Share available days and times, and update them when needed." },
            { icon: BookOpen,     title: "Teach in any format",  description: "Offer 1-on-1 sessions, group classes or crash courses — or a mix of all three." },
            { icon: Award,        title: "Show your experience", description: "Add relevant qualifications, teaching background and approach to your profile." },
            { icon: Inbox,        title: "Manage enquiries",     description: "Reply to students through the platform inbox. All conversations in one place." },
            { icon: Link2,        title: "Custom profile link",  description: "Share your teaching page with potential learners directly." },
          ]}
          after={[
            "Results depend on factors such as subject demand, pricing, profile quality and student interest. BookMyCoaching provides a place to present your work, but does not guarantee student numbers or income.",
          ]}
        />

        {/* Formats */}
        <FormatsSection
          id="formats"
          title="Choose How You Want to Teach"
          intro="Choose a format that suits your subject, student needs and availability. Plan lessons you can deliver consistently."
          formats={[
            {
              icon: User,
              title: "1-on-1 Sessions",
              description: "Offer individual attention through recurring lessons or a fixed-session package. This format suits learners who need help with specific topics at their own pace.",
              colorClass: "text-blue-600",
              borderColor: "#2F6BD8",
            },
            {
              icon: Users,
              title: "Group Classes",
              description: "Teach several learners on a shared timetable. Set the batch size and fee per student, and explain the class level and goals.",
              colorClass: "text-teal-700",
              borderColor: "#0E8A78",
            },
            {
              icon: Timer,
              title: "Crash Courses",
              description: "Create a time-bound programme for exam revision or selected topics. State the session count, dates, focus areas and full package price.",
              colorClass: "text-red-600",
              borderColor: "#D9472B",
            },
          ]}
          after={[
            "Make each listing clear about what is included, when sessions run and what students should prepare. Clear details help learners decide whether to enquire.",
          ]}
        />

        {/* Steps */}
        <StepsSection
          id="steps"
          title="How to Become an Online Tutor"
          intro="If you are researching how to become an online tutor in India, begin with a clear profile and a useful teaching offer."
          shaded
          steps={[
            { title: "Create a teacher account",  description: "Start registration and complete the required verification steps." },
            { title: "Build your profile",        description: "Add your subjects, experience, qualifications and teaching approach." },
            { title: "Submit for review",         description: "Send your profile for admin review and approval." },
            { title: "Choose a format",           description: "Offer 1-on-1 sessions, group classes, crash courses or a mix." },
            { title: "Publish an offering",       description: "Add the description, mode, schedule and pricing, then submit it for review." },
            { title: "Reply to enquiries",        description: "Check your inbox and respond to interested students after your offering is published." },
          ]}
          after={[
            "Use accurate information and explain your classes clearly. Check spelling, prices, subject names and availability before submitting.",
          ]}
          cta={BECOME_TUTOR}
        />

        {/* Tools */}
        <SplitSection
          id="tools"
          title="Tools to Manage Your Tutoring Presence"
          imageLeft
          imagePlaceholder="BookMyCoaching tutor dashboard with analytics and inbox visible in a browser frame"
          paras={[
            "Your tutor dashboard brings your profile, classes, enquiries and performance information together. The online teaching platform for tutors includes tools to present your work and manage student interest.",
          ]}
          defs={[
            { icon: BadgeCheck,  title: "Verified teacher profile",   description: "Present your qualifications and teaching experience after review." },
            { icon: Video,       title: "Introductory video",         description: "Give students a sense of your teaching style before they enquire." },
            { icon: FileText,    title: "Offering builder",           description: "Add subjects, formats, fees and availability to each listing." },
            { icon: Inbox,       title: "Inbox and messaging",        description: "Read and reply to student enquiries from one place." },
            { icon: BarChart3,   title: "Analytics",                  description: "Review profile views, offering views and student interest over time." },
            { icon: Link2,       title: "Custom profile link",        description: "Share your teaching page with potential learners directly." },
          ]}
          paras2={[
            "Keep your availability and prices current. Clear listings and timely replies help students decide whom to contact.",
          ]}
        />

        {/* Advanced tools panel */}
        <PanelSection
          id="tools-advanced"
          title="Advanced Tools for Growing Tutors"
          level="h3"
          intro="Tutors managing several classes or larger groups may need more ways to organise their work. BookMyCoaching lists additional tools through Gold and Platinum subscriptions."
          paras={[
            "Review current subscription details before choosing a plan. An online teaching platform for tutors is most useful when its features solve a real day-to-day need.",
          ]}
          boxTitle="Depending on the subscription, tools may include:"
          items={[
            "Class Management Hub: Organise class-related tasks",
            "Student roster and attendance: Track enrolled learners and attendance",
            "Session notes and summaries: Keep records of topics covered",
            "Automated reminders: Help manage upcoming sessions",
            "Broadcast emails and boosted search visibility: Share updates and access additional visibility options",
          ]}
          badges
          panelLink={{ label: "See Gold and Platinum plans", to: "/pricing" }}
        />

        {/* Profile tips */}
        <SplitSection
          id="profile"
          title="Build a Professional and Trusted Tutor Profile"
          imagePlaceholder="Teacher's BookMyCoaching profile page on a laptop screen, qualifications and bio visible"
          shaded
          paras={[
            "Students want to understand who will teach them before sending an enquiry. A complete profile helps them decide whether your classes match their goals.",
          ]}
          defs={[
            { icon: Award,      title: "Add relevant qualifications",  description: "Include education, training and teaching work related to your subjects." },
            { icon: ImageIcon,  title: "Use a clear profile picture",  description: "Choose a recent image where your face is easy to see." },
            { icon: Video,      title: "Record an introductory video", description: "Explain what you teach and how you help students." },
            { icon: FileText,   title: "Write specific descriptions",  description: "Include the subject, level, format, schedule and fee in each listing." },
            { icon: RefreshCw,  title: "Keep details updated",         description: "Correct old timings and changes to your offerings promptly." },
            { icon: Star,       title: "Encourage honest reviews",     description: "Genuine feedback helps future learners understand the experience." },
          ]}
          paras2={[
            "Before you register as an online tutor, gather the information you need and check that every claim is accurate. A good profile helps students understand your offer and decide whether to contact you.",
          ]}
        />

        <CtaBlock
          title="Ready to Start Your Tutoring Journey?"
          body="Share your teaching experience with learners looking for guidance. Create your profile, choose your offerings, and set fees and timings that work for you. Start with one well-planned class and improve it as you learn what learners need."
          cta={BECOME_TUTOR}
        />

        <FaqSection faqs={faqs} cta={BECOME_TUTOR} />

        <RelatedPages pages={RELATED} />
      </main>
      <Footer />
    </div>
  );
}
